import { useRef, useState, useCallback, useEffect } from 'react';
import { sanitizeRichHtml } from '../../utils/sanitizeRichHtml';

interface RichTextAreaProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  rows?: number;
  className?: string;
  disabled?: boolean;
}

const EMOJIS = [
  '😊','😂','🎉','👍','👎','❤️','🔥','✅','⚠️','❌',
  '🚀','💡','📌','📋','🐛','💻','📊','🔴','🟡','🟢',
  '⭐','🔒','🔓','📝','🔧','🔗','💬','🎯','📅','🏆',
];

export default function RichTextArea({
  value,
  onChange,
  placeholder,
  rows = 4,
  className = '',
  disabled = false,
}: RichTextAreaProps) {
  const editorRef = useRef<HTMLDivElement>(null);
  const [focused, setFocused] = useState(false);
  const [showEmoji, setShowEmoji] = useState(false);
  const isInternalUpdate = useRef(false);

  // Sync external value → editor innerHTML (only when the value changes externally)
  useEffect(() => {
    if (isInternalUpdate.current) {
      isInternalUpdate.current = false;
      return;
    }
    const el = editorRef.current;
    if (!el) return;
    const sanitizedValue = sanitizeRichHtml(value);
    if (el.innerHTML !== sanitizedValue) {
      el.innerHTML = sanitizedValue;
    }
  }, [value]);

  const emitChange = useCallback(() => {
    const el = editorRef.current;
    if (!el) return;
    isInternalUpdate.current = true;
    onChange(sanitizeRichHtml(el.innerHTML));
  }, [onChange]);

  const execCmd = useCallback((command: string, val?: string) => {
    document.execCommand(command, false, val);
    emitChange();
  }, [emitChange]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if ((e.ctrlKey || e.metaKey) && !e.shiftKey) {
        switch (e.key.toLowerCase()) {
          case 'b':
            e.preventDefault();
            execCmd('bold');
            break;
          case 'i':
            e.preventDefault();
            execCmd('italic');
            break;
          case 'u':
            e.preventDefault();
            execCmd('underline');
            break;
        }
      }
    },
    [execCmd]
  );

  const showToolbar = focused && !disabled;

  const btn = (title: string, action: () => void, content: React.ReactNode, extraCls = '') => (
    <button
      title={title}
      onMouseDown={(e) => { e.preventDefault(); action(); }}
      className={`p-1 rounded text-gray-500 hover:bg-gray-100 hover:text-gray-800 transition-colors flex items-center justify-center min-w-[22px] h-[22px] text-[12px] ${extraCls}`}
    >
      {content}
    </button>
  );

  const divider = <div className="w-px h-4 bg-gray-200 mx-0.5 self-center shrink-0" />;

  const minHeight = rows * 20;

  return (
    <div className="relative w-full">
      {/* Formatting toolbar */}
      {showToolbar && (
        <div
          className="flex items-center flex-wrap gap-0.5 border border-[#c8c8c8] border-b-0 rounded-t-[3px] bg-[#f8f8f8] px-2 py-1"
          onMouseDown={(e) => e.preventDefault()}
        >
          {btn('Bold (Ctrl+B)', () => execCmd('bold'), <span className="font-bold leading-none">B</span>)}
          {btn('Italic (Ctrl+I)', () => execCmd('italic'), <span className="italic leading-none">I</span>)}
          {btn('Underline (Ctrl+U)', () => execCmd('underline'), <span className="underline leading-none">U</span>)}
          {btn('Strikethrough', () => execCmd('strikeThrough'), <span style={{ textDecoration: 'line-through' }} className="leading-none">S</span>)}

          {divider}

          {btn('Bullet list', () => execCmd('insertUnorderedList'),
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="4" cy="7" r="1.5" fill="currentColor" stroke="none"/>
              <circle cx="4" cy="12" r="1.5" fill="currentColor" stroke="none"/>
              <circle cx="4" cy="17" r="1.5" fill="currentColor" stroke="none"/>
              <line x1="8" y1="7" x2="21" y2="7"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="17" x2="21" y2="17"/>
            </svg>
          )}
          {btn('Numbered list', () => execCmd('insertOrderedList'),
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="10" y1="7" x2="21" y2="7"/><line x1="10" y1="12" x2="21" y2="12"/><line x1="10" y1="17" x2="21" y2="17"/>
              <text x="2" y="9" fontSize="7" fill="currentColor" stroke="none" fontFamily="sans-serif">1.</text>
              <text x="2" y="14" fontSize="7" fill="currentColor" stroke="none" fontFamily="sans-serif">2.</text>
              <text x="2" y="19" fontSize="7" fill="currentColor" stroke="none" fontFamily="sans-serif">3.</text>
            </svg>
          )}

          {divider}

          {btn('Decrease indent', () => execCmd('outdent'),
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="21" y1="7" x2="9" y2="7"/><line x1="21" y1="12" x2="3" y2="12"/><line x1="21" y1="17" x2="9" y2="17"/>
              <polyline points="7 9 3 12 7 15"/>
            </svg>
          )}
          {btn('Increase indent', () => execCmd('indent'),
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="21" y1="7" x2="9" y2="7"/><line x1="21" y1="12" x2="3" y2="12"/><line x1="21" y1="17" x2="9" y2="17"/>
              <polyline points="3 9 7 12 3 15"/>
            </svg>
          )}

          {divider}

          {btn('Highlight', () => {
            const current = document.queryCommandValue('hiliteColor');
            const isHighlighted = current && current !== 'transparent' && current !== 'rgba(0, 0, 0, 0)';
            execCmd('hiliteColor', isHighlighted ? 'transparent' : '#fef08a');
          },
            <span className="leading-none px-0.5 bg-yellow-200 rounded-sm font-semibold">H</span>
          )}

          {divider}

          {/* Emoji */}
          <div className="relative">
            <button
              title="Emoji"
              onMouseDown={(e) => { e.preventDefault(); setShowEmoji((v) => !v); }}
              className="p-1 rounded text-gray-500 hover:bg-gray-100 hover:text-gray-800 transition-colors flex items-center justify-center min-w-[22px] h-[22px] text-[14px]"
            >
              😊
            </button>
            {showEmoji && (
              <div
                className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded shadow-lg p-2 z-50"
                style={{ width: 180 }}
                onMouseDown={(e) => e.preventDefault()}
              >
                <div className="grid grid-cols-6 gap-0.5">
                  {EMOJIS.map((e) => (
                    <button
                      key={e}
                      onMouseDown={(ev) => { ev.preventDefault(); execCmd('insertText', e); }}
                      className="text-[16px] p-0.5 hover:bg-gray-100 rounded flex items-center justify-center"
                    >
                      {e}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ContentEditable editor */}
      <div
        ref={editorRef}
        contentEditable={!disabled}
        suppressContentEditableWarning
        onInput={emitChange}
        onFocus={() => setFocused(true)}
        onBlur={() => { setTimeout(() => { setFocused(false); setShowEmoji(false); }, 250); }}
        onKeyDown={handleKeyDown}
        data-placeholder={placeholder}
        style={{ minHeight }}
        className={`${className} ${showToolbar ? '!rounded-t-none !border-t-0' : ''} overflow-y-auto empty:before:content-[attr(data-placeholder)] empty:before:text-gray-400 empty:before:pointer-events-none outline-none rich-editor-content`}
      />
    </div>
  );
}
